import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { AnalysisResult } from '../types';
import { InjuryTooltip } from './InjuryTooltip';

interface InjuryPreventionChartProps {
  result: AnalysisResult;
}

export const InjuryPreventionChart: React.FC<InjuryPreventionChartProps> = ({ result }) => {
  const { injuryPrevention } = result;
  
  if (!injuryPrevention) return null;

  const data = [
    {
      metric: 'Power Balance',
      value: 100 - injuryPrevention.powerAsymmetry,
      risk: injuryPrevention.powerAsymmetry
    },
    {
      metric: 'Fatigue Resistance',
      value: injuryPrevention.fatigueThreshold,
      risk: 100 - injuryPrevention.fatigueThreshold
    },
    {
      metric: 'Recovery Status',
      value: 100 - injuryPrevention.overreachingRisk,
      risk: injuryPrevention.overreachingRisk
    },
    {
      metric: 'Technique Stability',
      value: 100 - injuryPrevention.techniqueBreakdown,
      risk: injuryPrevention.techniqueBreakdown
    },
    {
      metric: 'Load Capacity',
      value: injuryPrevention.loadTolerance,
      risk: 100 - injuryPrevention.loadTolerance
    }
  ];

  const getRiskLevel = (risk: number) => {
    if (risk < 25) return { level: 'Low', color: 'text-green-400' };
    if (risk < 50) return { level: 'Moderate', color: 'text-yellow-400' };
    if (risk < 75) return { level: 'High', color: 'text-orange-400' };
    return { level: 'Critical', color: 'text-red-400' };
  };

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Injury Prevention Profile</h3>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <PolarGrid stroke="#374151" />
            <PolarAngleAxis 
              dataKey="metric" 
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]} 
              tick={{ fill: '#9CA3AF', fontSize: 10 }}
              tickCount={5}
            />
            <Radar
              name="Safety Score"
              dataKey="value"
              stroke="#10B981"
              fill="#10B981"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Radar
              name="Risk Level"
              dataKey="risk"
              stroke="#EF4444"
              fill="#EF4444"
              fillOpacity={0.1}
              strokeWidth={1}
              strokeDasharray="5 5"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151', 
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
              formatter={(value: number, name: string, props: any) => {
                const metric = props.payload.metric;
                const riskLevel = value < 25 ? 'Low' : value < 50 ? 'Moderate' : value < 75 ? 'High' : 'Critical';
                return [
                  `${value.toFixed(1)}% (${riskLevel} Risk)`,
                  name === 'value' ? 'Safety Score' : 'Risk Level'
                ];
              }}
              labelFormatter={(label) => `${label} Analysis`}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Detailed Insights */}
      <div className="mt-6 p-4 bg-theme-surface-2 border border-theme-border rounded-xl">
        <h4 className="font-semibold text-theme-text mb-3">Calculation Methods & Training Implications</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-theme-surface-1 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-theme-text">Power Balance</span>
              <span className={`text-xs px-2 py-1 rounded ${getRiskLevel(injuryPrevention.powerAsymmetry).color} bg-opacity-20`}>
                {injuryPrevention.powerAsymmetry.toFixed(1)}% asymmetry
              </span>
            </div>
            <p className="text-xs text-theme-muted mb-2">
              <strong>Calculation:</strong> Power CV = (SD/Mean) × 100 from all trials
            </p>
            <p className="text-xs text-theme-muted">
              {injuryPrevention.powerAsymmetry < 15 ? 
                "Excellent bilateral balance. Continue current training approach." :
                injuryPrevention.powerAsymmetry < 30 ?
                "Moderate imbalance. Add single-leg exercises 2x/week." :
                "High asymmetry risk. Prioritize unilateral training and movement screening."}
            </p>
          </div>
          
          <div className="p-3 bg-theme-surface-1 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-theme-text">Fatigue Resistance</span>
              <span className={`text-xs px-2 py-1 rounded ${getRiskLevel(100 - injuryPrevention.fatigueThreshold).color} bg-opacity-20`}>
                {injuryPrevention.fatigueThreshold.toFixed(0)}% threshold
              </span>
            </div>
            <p className="text-xs text-theme-muted mb-2">
              <strong>Calculation:</strong> 100 - (Power decline % × 2) across session
            </p>
            <p className="text-xs text-theme-muted">
              {injuryPrevention.fatigueThreshold > 75 ?
                "High resistance. Can progress training loads by 5-10%." :
                injuryPrevention.fatigueThreshold > 50 ?
                "Moderate resistance. Maintain current loads, add recovery protocols." :
                "Low resistance. Deload 20-30%, focus on sleep and nutrition."}
            </p>
          </div>
          
          <div className="p-3 bg-theme-surface-1 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-theme-text">Recovery Status</span>
              <span className={`text-xs px-2 py-1 rounded ${getRiskLevel(injuryPrevention.overreachingRisk).color} bg-opacity-20`}>
                {injuryPrevention.overreachingRisk.toFixed(0)}% risk
              </span>
            </div>
            <p className="text-xs text-theme-muted mb-2">
              <strong>Calculation:</strong> Deviation from expected F-V relationship × 3
            </p>
            <p className="text-xs text-theme-muted">
              {injuryPrevention.overreachingRisk < 25 ?
                "Normal patterns. Continue current training schedule." :
                injuryPrevention.overreachingRisk < 50 ?
                "Mild overreaching. Add 1-2 extra rest days this week." :
                "Overreaching detected. Implement deload week immediately."}
            </p>
          </div>
          
          <div className="p-3 bg-theme-surface-1 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-theme-text">Technique Stability</span>
              <span className={`text-xs px-2 py-1 rounded ${getRiskLevel(injuryPrevention.techniqueBreakdown).color} bg-opacity-20`}>
                {(100 - injuryPrevention.techniqueBreakdown).toFixed(0)}% stable
              </span>
            </div>
            <p className="text-xs text-theme-muted mb-2">
              <strong>Calculation:</strong> 100 - min(Velocity consistency, R² × 100)
            </p>
            <p className="text-xs text-theme-muted">
              {injuryPrevention.techniqueBreakdown < 25 ?
                "Stable technique. Maintain current movement quality focus." :
                injuryPrevention.techniqueBreakdown < 50 ?
                "Some variation. Add video analysis and tempo work." :
                "Breakdown evident. Reduce loads 20-30%, focus on form."}
            </p>
          </div>
          
          <div className="p-3 bg-theme-surface-1 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-theme-text">Load Capacity</span>
              <span className={`text-xs px-2 py-1 rounded ${getRiskLevel(100 - injuryPrevention.loadTolerance).color} bg-opacity-20`}>
                {injuryPrevention.loadTolerance.toFixed(0)}% capacity
              </span>
            </div>
            <p className="text-xs text-theme-muted mb-2">
              <strong>Calculation:</strong> (Max load / 40kg) × 100, capped at 100%
            </p>
            <p className="text-xs text-theme-muted">
              {injuryPrevention.loadTolerance > 75 ?
                "High tolerance. Progress loads +5kg every 2-3 weeks." :
                injuryPrevention.loadTolerance > 50 ?
                "Moderate capacity. Progress +2.5kg weekly with good form." :
                "Limited tolerance. Build base strength with 3-6 rep ranges."}
            </p>
          </div>
        </div>
      </div>
      
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <h4 className="font-semibold text-theme-text mb-2">Risk Assessment</h4>
          <ul className="space-y-1 text-theme-muted">
            <li>
              <InjuryTooltip metric="powerAsymmetry" value={injuryPrevention.powerAsymmetry}>
                <span className="cursor-help border-b border-dotted border-theme-muted">
                  Power Asymmetry: <span className={getRiskLevel(injuryPrevention.powerAsymmetry).color}>{getRiskLevel(injuryPrevention.powerAsymmetry).level}</span>
                </span>
              </InjuryTooltip>
            </li>
            <li>
              <InjuryTooltip metric="overreachingRisk" value={injuryPrevention.overreachingRisk}>
                <span className="cursor-help border-b border-dotted border-theme-muted">
                  Overreaching Risk: <span className={getRiskLevel(injuryPrevention.overreachingRisk).color}>{getRiskLevel(injuryPrevention.overreachingRisk).level}</span>
                </span>
              </InjuryTooltip>
            </li>
            <li>
              <InjuryTooltip metric="techniqueBreakdown" value={injuryPrevention.techniqueBreakdown}>
                <span className="cursor-help border-b border-dotted border-theme-muted">
                  Technique Risk: <span className={getRiskLevel(injuryPrevention.techniqueBreakdown).color}>{getRiskLevel(injuryPrevention.techniqueBreakdown).level}</span>
                </span>
              </InjuryTooltip>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-theme-text mb-2">Recommendations</h4>
          <ul className="space-y-1 text-theme-muted text-xs">
            {injuryPrevention.powerAsymmetry > 50 && <li>• Focus on bilateral strength training</li>}
            {injuryPrevention.fatigueThreshold < 50 && <li>• Reduce training volume</li>}
            {injuryPrevention.overreachingRisk > 50 && <li>• Increase recovery time</li>}
            {injuryPrevention.techniqueBreakdown > 50 && <li>• Technical skill work needed</li>}
            {injuryPrevention.loadTolerance < 50 && <li>• Progressive load increase</li>}
          </ul>
        </div>
      </div>
    </div>
  );
};